import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  MapPin,
  Building2,
  Lock,
  MoreHorizontal,
  Gift,
  DollarSign,
  ArrowRight,
  ArrowLeft,
  Search,
  CheckCircle,
} from 'lucide-react';
import './AddParkingRecsModal.css';
import type { LatLngTuple } from 'leaflet';
import type { Location } from '../../types';
import { searchPlaces, type GeoapifyPlace } from '../../services/geoapify';

type ParkingCategory = 'street' | 'car-park' | 'private' | 'other';
type PricingType = 'free' | 'paid';

// Unified search result type
interface UnifiedSearchResult {
  id: string;
  name: string;
  displayName: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  distance?: number; // in meters
  isVibeCheckSupported: boolean; // true if from local locations
  placeId?: string; // Geoapify place_id if external
  source: 'local' | 'geoapify';
}

interface AddParkingRecsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    category: ParkingCategory;
    pricing: PricingType;
    details?: {
      price?: string;
      paymentMethod?: string;
      meteredTimes?: string;
      difficultyLevel?: number;
      accessibleParking?: boolean;
      evCharging?: boolean;
      heightRestrictions?: string;
      notes?: string;
    };
    recommendedLocation?: {
      id: string;
      name?: string;
      displayName?: string;
      address?: string;
      latitude?: number;
      longitude?: number;
      distance?: number;
      source: 'local' | 'geoapify';
      isVibeCheckSupported: boolean;
    } | null;
    referenceLocation?: {
      label?: string | null;
      coordinates?: LatLngTuple | null;
    } | null;
  }) => void;
  allLocations?: Record<string, Location> | Location[]; // Available locations from map
  currentLocation?: LatLngTuple | null; // Current user location for auto-selection
  referenceLocation?: LatLngTuple | null; // Location selected in RecommendationsFAB for distance ranking
  referenceLocationName?: string | null;
}

const PARKING_CATEGORIES: Array<{ id: ParkingCategory; label: string; icon: React.ComponentType<{ size?: number }> }> =
  [
    { id: 'street', label: 'Street', icon: MapPin },
    { id: 'car-park', label: 'Car Park', icon: Building2 },
    { id: 'private', label: 'Private', icon: Lock },
    { id: 'other', label: 'Other', icon: MoreHorizontal },
  ];

// Helper function to calculate distance between two coordinates (Haversine formula)
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Convert locations object/array to array format
// allLocations is an object where keys are location IDs/names and values are location objects
const normalizeLocations = (locations: Record<string, any> | Location[] | undefined): Location[] => {
  if (!locations) return [];
  if (Array.isArray(locations)) {
    // If it's already an array, ensure each item has required fields
    return locations.map((loc) => ({
      _id: loc._id || loc.location || String(loc),
      location: loc.location || loc._id || String(loc),
      latitude: loc.latitude,
      longitude: loc.longitude,
      category: loc.category || 'Park',
      ...loc,
    }));
  }
  // If it's an object, convert to array and ensure each has _id and location set to the key
  return Object.entries(locations).map(([key, loc]: [string, any]) => ({
    _id: key,
    location: key, // Use the key as the location name
    latitude: loc.latitude,
    longitude: loc.longitude,
    category: loc.category || 'Park',
    busyness: loc.busyness || 0,
    vibeRating: loc.vibeRating || 0,
    communityRecommendations: loc.communityRecommendations || [],
    parkingOptions: loc.parkingOptions || [],
    capacities: loc.capacities || {},
    shops: loc.shops,
    ...loc, // Spread other properties
  }));
};

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const AddParkingRecsModal: React.FC<AddParkingRecsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  allLocations,
  currentLocation,
  referenceLocation,
  referenceLocationName,
}) => {
  const [step, setStep] = React.useState<1 | 2 | 3 | 4>(1);
  const [category, setCategory] = React.useState<ParkingCategory | null>(null);
  const [selectedLocation, setSelectedLocation] = React.useState<string | null>(null);
  const [selectedLocationData, setSelectedLocationData] = React.useState<UnifiedSearchResult | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [unifiedSearchResults, setUnifiedSearchResults] = React.useState<UnifiedSearchResult[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [pricing, setPricing] = React.useState<PricingType | null>(null);
  const [details, setDetails] = React.useState<{
    price?: string;
    paymentMethod?: string;
    meteredTimes?: string;
    difficultyLevel?: number;
    accessibleParking?: boolean;
    evCharging?: boolean;
    heightRestrictions?: string;
    notes?: string;
  }>({
    difficultyLevel: 3,
    accessibleParking: false,
    evCharging: false,
    heightRestrictions: 'No limit',
  });
  const [showSearch, setShowSearch] = React.useState(false);
  const [isCustomizingLocation, setIsCustomizingLocation] = React.useState(false);
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  // Reset when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setCategory(null);
      setSelectedLocation(null);
      setSelectedLocationData(null);
      setSearchQuery('');
      setUnifiedSearchResults([]);
      setPricing(null);
      setDetails({
        difficultyLevel: 3,
        accessibleParking: false,
        evCharging: false,
        heightRestrictions: 'No limit',
      });
      setShowSearch(false);
      setIsCustomizingLocation(false);
    }
  }, [isOpen]);

  const locationsArray = React.useMemo(() => {
    return normalizeLocations(allLocations);
  }, [allLocations]);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Lock body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore body scroll
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      };
    }
  }, [isOpen]);

  // Auto-select closest location when step 2 opens
  React.useEffect(() => {
    if (isCustomizingLocation) return;
    const baseLocation = referenceLocation || currentLocation;
    if (step === 2 && baseLocation && locationsArray.length > 0 && !selectedLocation) {
      const [userLat, userLng] = baseLocation;
      let closest: Location | null = null;
      let minDistance = Infinity;

      locationsArray.forEach((loc) => {
        if (loc.latitude && loc.longitude) {
          const distance = calculateDistance(userLat, userLng, loc.latitude, loc.longitude);
          if (distance < minDistance) {
            minDistance = distance;
            closest = loc;
          }
        }
      });

      if (closest) {
        const locationName = closest.location || closest._id;
        setSelectedLocation(locationName);
        setSelectedLocationData({
          id: closest._id,
          name: locationName,
          displayName: locationName,
          latitude: closest.latitude,
          longitude: closest.longitude,
          distance: minDistance * 1000,
          isVibeCheckSupported: true,
          source: 'local',
        });
        setIsCustomizingLocation(false);
        setShowSearch(false);
      }
    }
  }, [step, currentLocation, referenceLocation, locationsArray, selectedLocation, isCustomizingLocation]);

  // Unified search combining local and Geoapify results
  // Only run when modal is open and on step 2
  React.useEffect(() => {
    // Don't run search if modal is closed or not on step 2
    if (!isOpen || step !== 2) {
      setUnifiedSearchResults([]);
      setIsSearching(false);
      return;
    }

    let isCancelled = false;

    const performSearch = async () => {
      if (!debouncedSearchQuery || debouncedSearchQuery.trim().length < 2) {
        if (!isCancelled) {
          setUnifiedSearchResults([]);
          setIsSearching(false);
        }
        return;
      }

      if (!isCancelled) {
        setIsSearching(true);
      }

      const baseLocation = referenceLocation || currentLocation;
      const queryLower = debouncedSearchQuery.toLowerCase().trim();
      const results: UnifiedSearchResult[] = [];

      // 1. Search local locations
      const filtered = locationsArray.filter((loc) => {
        const locationName = (loc.location || loc._id || '').toLowerCase();
        const locationId = (loc._id || '').toLowerCase();
        const category = (loc.category || '').toLowerCase();
        return locationName.includes(queryLower) || locationId.includes(queryLower) || category.includes(queryLower);
      });

      // Convert local results to unified format
      filtered.forEach((loc) => {
        const locationName = loc.location || loc._id;
        let distance: number | undefined;

        if (baseLocation && Array.isArray(baseLocation) && baseLocation.length === 2 && loc.latitude && loc.longitude) {
          const [refLat, refLng] = baseLocation;
          distance = calculateDistance(refLat, refLng, loc.latitude, loc.longitude) * 1000; // Convert to meters
        }

        results.push({
          id: loc._id,
          name: locationName,
          displayName: locationName,
          latitude: loc.latitude,
          longitude: loc.longitude,
          distance,
          isVibeCheckSupported: true,
          source: 'local',
        });
      });

      // 2. Search Geoapify (only if we have API key and query is valid)
      try {
        console.log('🌐 Searching Geoapify for:', debouncedSearchQuery);
        const geoapifyPlaces = await searchPlaces(
          debouncedSearchQuery,
          baseLocation ? { latitude: baseLocation[0], longitude: baseLocation[1] } : undefined,
          10
        );

        console.log('🌐 Geoapify returned:', geoapifyPlaces.length, 'places');
        if (geoapifyPlaces.length > 0) {
          console.log('🌐 First result:', geoapifyPlaces[0]);
        }

        // Convert Geoapify results to unified format
        geoapifyPlaces.forEach((place) => {
          results.push({
            id: place.place_id,
            name: place.name,
            displayName: place.name,
            address: place.formatted,
            latitude: place.lat,
            longitude: place.lon,
            distance: place.distance,
            isVibeCheckSupported: false,
            placeId: place.place_id,
            source: 'geoapify',
          });
        });
      } catch (error) {
        console.error('❌ Error searching Geoapify:', error);
      }

      console.log(
        '📊 Total unified results:',
        results.length,
        '(Local:',
        results.filter((r) => r.source === 'local').length,
        'Geoapify:',
        results.filter((r) => r.source === 'geoapify').length,
        ')'
      );

      // Only update state if the effect hasn't been cancelled
      if (!isCancelled) {
        // Sort unified results: VibeCheck locations first, then by distance
        const sorted = results.sort((a, b) => {
          // VibeCheck-supported locations first
          if (a.isVibeCheckSupported && !b.isVibeCheckSupported) return -1;
          if (!a.isVibeCheckSupported && b.isVibeCheckSupported) return 1;

          // Then by distance if available
          if (a.distance !== undefined && b.distance !== undefined) {
            return a.distance - b.distance;
          }
          if (a.distance !== undefined) return -1;
          if (b.distance !== undefined) return 1;

          // Finally by name
          return a.displayName.localeCompare(b.displayName);
        });

        setUnifiedSearchResults(sorted.slice(0, 15)); // Limit to 15 total results
        setIsSearching(false);
      }
    };

    performSearch();

    // Cleanup function to cancel the search if component unmounts or dependencies change
    return () => {
      isCancelled = true;
    };
  }, [debouncedSearchQuery, referenceLocation, currentLocation, locationsArray, isOpen, step]);

  // Handle search input change (without debouncing for immediate UI feedback)
  const handleSearchInputChange = (query: string) => {
    setSearchQuery(query);
    if (!query || query.trim().length < 2) {
      setUnifiedSearchResults([]);
    }
  };

  const canSubmit = !!category && !!pricing;
  const canNext = step === 1 ? !!category : step === 2 ? !!selectedLocation : step === 3 ? !!pricing : true;

  const submit = () => {
    if (!category || !pricing) return;

    const recommendedLocation = selectedLocationData
      ? {
          id: selectedLocationData.id,
          name: selectedLocationData.name,
          displayName: selectedLocationData.displayName,
          address: selectedLocationData.address,
          latitude: selectedLocationData.latitude,
          longitude: selectedLocationData.longitude,
          distance: selectedLocationData.distance,
          source: selectedLocationData.source,
          isVibeCheckSupported: selectedLocationData.isVibeCheckSupported,
        }
      : selectedLocation
      ? {
          id: selectedLocation,
          name: selectedLocation,
          displayName: selectedLocation,
          source: 'local' as const,
          isVibeCheckSupported: true,
        }
      : null;

    const referenceLocationPayload =
      referenceLocationName || referenceLocation
        ? {
            label: referenceLocationName || null,
            coordinates: referenceLocation || null,
          }
        : null;

    onSubmit({
      category,
      pricing,
      details,
      recommendedLocation,
      referenceLocation: referenceLocationPayload,
    });
    onClose();
    setStep(1);
    setCategory(null);
    setSelectedLocation(null);
    setSelectedLocationData(null);
    setSearchQuery('');
    setUnifiedSearchResults([]);
    setPricing(null);
    setDetails({
      difficultyLevel: 3,
      accessibleParking: false,
      evCharging: false,
      heightRestrictions: 'No limit',
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="recommendations-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="parking-modal"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Fixed Header */}
          <div className="parking-modal-header">
            <h2>
              {step === 1
                ? 'Select parking category'
                : step === 2
                ? 'Parking recommendation for'
                : step === 3
                ? 'Free or paid'
                : 'Parking details'}
            </h2>
            <button onClick={onClose} className="close-button">
              <X size={24} />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="parking-modal-content">
            {step === 1 && (
              <div className="parking-categories-scroll">
                <div className="parking-categories-grid">
                  {PARKING_CATEGORIES.map((cat) => {
                    const IconComponent = cat.icon;
                    return (
                      <motion.button
                        key={cat.id}
                        className={`parking-category ${category === cat.id ? 'selected' : ''}`}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setCategory(cat.id);
                          setStep(2);
                        }}
                      >
                        <div className={`parking-icon-circle gradient-${cat.id}`}>
                          <IconComponent size={22} />
                        </div>
                        <div className="parking-category-title">{cat.label}</div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="parking-location-selection">
                {referenceLocationName && (
                  <div className="reference-location-banner">
                    <div className="reference-banner-text">
                      <span className="reference-heading">Recommending “{referenceLocationName}”</span>
                      <span className="reference-context">
                        {selectedLocationData?.displayName || selectedLocation
                          ? `for parking at ${selectedLocationData?.displayName || selectedLocation}`
                          : 'Pick a parking spot below to complete this recommendation.'}
                      </span>
                    </div>

                    <div className="reference-actions">
                      {selectedLocation && (
                        <div className="selected-location-chip">
                          <MapPin size={16} />
                          <span>{selectedLocation}</span>
                          <button
                            className="clear-location-button"
                            onClick={() => {
                              setIsCustomizingLocation(true);
                              setSelectedLocation(null);
                              setSelectedLocationData(null);
                              setSearchQuery('');
                              setUnifiedSearchResults([]);
                              setShowSearch(true);
                              requestAnimationFrame(() => searchInputRef.current?.focus());
                            }}
                            aria-label="Clear selection"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}

                      <button
                        type="button"
                        className="reference-change-button"
                        onClick={() => {
                          setIsCustomizingLocation(true);
                          setSelectedLocation(null);
                          setSelectedLocationData(null);
                          setSearchQuery('');
                          setUnifiedSearchResults([]);
                          setShowSearch(true);
                          requestAnimationFrame(() => searchInputRef.current?.focus());
                        }}
                      >
                        Change location
                      </button>
                    </div>
                  </div>
                )}

                {/* Search Section */}
                {showSearch && (
                  <div className="location-search-container">
                    <div className="location-search-label">Search for another location</div>
                    <div className="location-search-input-wrapper">
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => handleSearchInputChange(e.target.value)}
                        placeholder="Search for a parking location..."
                        className="location-search-input"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                      />
                      {isSearching && (
                        <div className="search-loading-indicator">
                          <div className="search-spinner"></div>
                        </div>
                      )}
                    </div>
                    {searchQuery && unifiedSearchResults.length > 0 && (
                      <div className="location-search-results">
                        {unifiedSearchResults.map((result) => (
                          <button
                            key={result.id}
                            className={`location-result-item ${
                              selectedLocation === result.displayName ? 'selected' : ''
                            }`}
                            onClick={() => {
                              setSelectedLocation(result.displayName);
                              setSelectedLocationData(result);
                              setSearchQuery('');
                              setUnifiedSearchResults([]);
                              setShowSearch(false);
                              setIsCustomizingLocation(false);
                            }}
                          >
                            <MapPin size={16} />
                            <div className="location-result-content">
                              <div className="location-result-header">
                                <span className="location-result-name">{result.displayName}</span>
                                {result.isVibeCheckSupported && (
                                  <span className="vibechECK-badge" title="VibeCheck Supported Location">
                                    <CheckCircle size={14} />
                                    <span>VibeCheck</span>
                                  </span>
                                )}
                              </div>
                              {result.address && <span className="location-result-address">{result.address}</span>}
                              {result.distance !== undefined && (
                                <span className="location-result-distance">
                                  {result.distance < 1000
                                    ? `${Math.round(result.distance)}m away`
                                    : `${(result.distance / 1000).toFixed(1)}km away`}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {!selectedLocation &&
                      searchQuery &&
                      unifiedSearchResults.length === 0 &&
                      !isSearching &&
                      searchQuery.length >= 2 && (
                        <div className="no-locations-message">No locations found matching "{searchQuery}"</div>
                      )}
                  </div>
                )}

                {/* Empty State */}
                {!selectedLocation && !searchQuery && locationsArray.length === 0 && (
                  <div className="no-locations-message">No locations available. Please search for a location.</div>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="parking-pricing-options">
                <div className="pricing-options-grid">
                  <motion.button
                    className={`pricing-option ${pricing === 'free' ? 'selected' : ''}`}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setPricing('free');
                      setStep(4);
                    }}
                  >
                    <div className="pricing-icon-circle gradient-free">
                      <Gift size={22} />
                    </div>
                    <div className="pricing-option-title">Free</div>
                  </motion.button>
                  <motion.button
                    className={`pricing-option ${pricing === 'paid' ? 'selected' : ''}`}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setPricing('paid');
                      setStep(4);
                    }}
                  >
                    <div className="pricing-icon-circle gradient-paid">
                      <DollarSign size={22} />
                    </div>
                    <div className="pricing-option-title">Paid</div>
                  </motion.button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="parking-details">
                {pricing === 'paid' && (
                  <>
                    <div className="detail-group">
                      <label className="detail-label">Price/Cost (optional)</label>
                      <input
                        type="text"
                        value={details.price || ''}
                        onChange={(e) => setDetails({ ...details, price: e.target.value })}
                        placeholder="e.g., $2/hour, $15/day"
                        className="detail-input"
                      />
                      <div className="quick-select-chips">
                        {['$2/hr', '$3/hr', '$5/hr', '$10/hr', '$15/day', '$20/day', 'Free'].map((priceOption) => (
                          <button
                            key={priceOption}
                            type="button"
                            className={`quick-chip ${details.price === priceOption ? 'selected' : ''}`}
                            onClick={() => setDetails({ ...details, price: priceOption })}
                          >
                            {priceOption}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="detail-group">
                      <label className="detail-label">Payment Method (optional)</label>
                      <select
                        value={details.paymentMethod || 'meter'}
                        onChange={(e) => setDetails({ ...details, paymentMethod: e.target.value })}
                        className="detail-select"
                      >
                        <option value="meter">Meter</option>
                        <option value="app">App</option>
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                      </select>
                    </div>

                    <div className="detail-group">
                      <label className="detail-label">Metered Times / Conditions (optional)</label>
                      <textarea
                        value={details.meteredTimes || ''}
                        onChange={(e) => setDetails({ ...details, meteredTimes: e.target.value })}
                        placeholder="e.g., Mon-Fri 9am-5pm, 2P limit"
                        className="detail-textarea"
                        rows={2}
                      />
                      <div className="quick-select-chips">
                        {['Mon-Fri 9am-5pm', '2P limit', '4P limit', 'All day', 'Weekends free', '24/7'].map(
                          (timeOption) => (
                            <button
                              key={timeOption}
                              type="button"
                              className={`quick-chip ${details.meteredTimes === timeOption ? 'selected' : ''}`}
                              onClick={() => setDetails({ ...details, meteredTimes: timeOption })}
                            >
                              {timeOption}
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </>
                )}

                <div className="detail-group">
                  <label className="detail-label">Difficulty Level (optional)</label>
                  <div className="difficulty-slider">
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={details.difficultyLevel || 3}
                      onChange={(e) => setDetails({ ...details, difficultyLevel: parseInt(e.target.value) })}
                      className="range-input"
                    />
                    <div className="difficulty-value">{details.difficultyLevel || 3}/5</div>
                  </div>
                </div>

                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={details.accessibleParking || false}
                      onChange={(e) => setDetails({ ...details, accessibleParking: e.target.checked })}
                    />
                    <span>Accessible parking available</span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={details.evCharging || false}
                      onChange={(e) => setDetails({ ...details, evCharging: e.target.checked })}
                    />
                    <span>EV charging available</span>
                  </label>
                </div>

                <div className="detail-group">
                  <label className="detail-label">Height Restrictions (optional)</label>
                  <select
                    value={details.heightRestrictions || 'No limit'}
                    onChange={(e) => setDetails({ ...details, heightRestrictions: e.target.value })}
                    className="detail-select"
                  >
                    <option value="No limit">No limit</option>
                    <option value="2.0m">2.0m</option>
                    <option value="2.1m">2.1m</option>
                    <option value="2.2m">2.2m</option>
                    <option value="2.5m">2.5m</option>
                  </select>
                </div>

                <div className="detail-group">
                  <label className="detail-label">Additional Notes (optional)</label>
                  <textarea
                    value={details.notes || ''}
                    onChange={(e) => setDetails({ ...details, notes: e.target.value })}
                    placeholder="Any additional information..."
                    className="detail-textarea"
                    rows={3}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Fixed Footer */}
          <div className="parking-actions">
            <div className="left-actions">
              {step > 1 && (
                <button className="action-button secondary" onClick={() => setStep((s) => (s - 1) as 1 | 2 | 3)}>
                  <ArrowLeft size={18} />
                  Back
                </button>
              )}
            </div>
            <div className="right-actions">
              {step === 2 ? (
                <button
                  className={`action-button primary ${canNext ? 'active' : ''}`}
                  disabled={!canNext}
                  onClick={() => setStep(3)}
                >
                  Next
                  <ArrowRight size={18} />
                </button>
              ) : step === 3 ? (
                <button
                  className={`action-button primary ${canNext ? 'active' : ''}`}
                  disabled={!canNext}
                  onClick={() => setStep(4)}
                >
                  Next
                  <ArrowRight size={18} />
                </button>
              ) : step === 4 ? (
                <button
                  className={`action-button primary ${canSubmit ? 'active' : ''}`}
                  disabled={!canSubmit}
                  onClick={submit}
                >
                  Submit
                </button>
              ) : null}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddParkingRecsModal;
